import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'magtic_cookie_consent';

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultConsent, ...parsed };
      }
    } catch (e) {}
    return { ...defaultConsent };
  });

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch (e) {}
  }, [consent]);

  const hasUserConsented = consent.timestamp != null;

  useEffect(() => {
    if (!hasUserConsented) setShowBanner(true);
  }, [hasUserConsented]);

  const setConsent = useCallback((next) => {
    setConsentState((prev) => {
      const nextConsent = typeof next === 'function' ? next(prev) : { ...prev, ...next };
      if (nextConsent.necessary === undefined) nextConsent.necessary = true;
      if (nextConsent.timestamp === undefined) nextConsent.timestamp = Date.now();
      return nextConsent;
    });
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    setConsent({ necessary: true, analytics: true, marketing: true, timestamp: Date.now() });
    setShowBanner(false);
  }, [setConsent]);

  const rejectOptional = useCallback(() => {
    setConsent({ necessary: true, analytics: false, marketing: false, timestamp: Date.now() });
    setShowBanner(false);
  }, [setConsent]);

  const openSettings = useCallback(() => setShowBanner(true), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        setConsent,
        showBanner,
        openSettings,
        acceptAll,
        rejectOptional,
        hasUserConsented,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
